import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/auth';
import { PaperSection } from '../components/PaperSection';

// Import the icons from Lucide React
import { Instagram, Apple } from 'lucide-react';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { signUp, signInWithProvider } = useAuthContext();
  const navigate = useNavigate();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password confirmation
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    try {
      setError(null);
      await signUp({ email, password });
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    }
  };

  const handleSocialSignIn = async (provider: 'apple' | 'google') => {
    try {
      setError(null);
      await signInWithProvider(provider);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An error occurred during social login');
    }
  };

  return (
    <div className="min-h-screen paper-texture-super-heavy px-4 sm:px-6 py-8 sm:py-12 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl font-display layout-logo">STYLOS</h1>
          <p className="text-gray-600 text-base sm:text-lg">Your Personal AI Fashion Stylist</p>
        </div>

        <div className="w-full">
          {error && (
            <PaperSection variant="very-heavy" className="text-red-500 border-l-4 border-red-400 mb-4 sm:mb-6 text-sm sm:text-base">
              {error}
            </PaperSection>
          )}

          <PaperSection variant="very-heavy" className="p-5 sm:p-6 rounded-lg">
            <form onSubmit={handleSignUp} className="space-y-4 sm:space-y-6">
              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/70 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-[16px]"
                  required
                />
              </div>

              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/70 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-[16px]"
                  required
                />
              </div>
              
              <div className="space-y-1 sm:space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/70 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-[16px]"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-black text-white py-2.5 sm:py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors text-[16px]"
              >
                Sign Up
              </button>
            </form>
          </PaperSection>

          {/* Social login options hidden as requested 
          <div className="my-6 text-center text-gray-500">or</div>

          <div className="space-y-4">
            <button
              onClick={() => handleSocialSignIn('google')}
              className="w-full flex items-center justify-center gap-3 border border-gray-200 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Instagram size={20} />
              <span>Continue with Google</span>
            </button>

            <button
              onClick={() => handleSocialSignIn('apple')}
              className="w-full flex items-center justify-center gap-3 border border-gray-200 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Apple size={20} />
              <span>Continue with Apple</span>
            </button>
          </div>
          */}

          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-gray-600 text-sm sm:text-base">
              Already have an account?{' '}
              <Link to="/login" className="text-black font-semibold">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 